import { ORDER_STATUS_TRANSITIONS } from '@adop/shared';

export type BadgeTone = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'outline';

export interface OrderAction {
  /** Target order status. */
  target: string;
  label: string;
  tone: 'primary' | 'secondary' | 'danger';
  /** Requires a confirmation dialog with a free-text reason. */
  confirm?: boolean;
}

const ACTION_META: Record<string, Omit<OrderAction, 'target'>> = {
  accepted: { label: 'Accept', tone: 'primary' },
  ready: { label: 'Mark ready', tone: 'primary' },
  completed: { label: 'Complete', tone: 'secondary' },
  cancelled: { label: 'Cancel order', tone: 'danger', confirm: true },
};

/** Action buttons allowed from a given status, per the shared transition table. */
export function orderActionsFor(status: string): OrderAction[] {
  const targets = ORDER_STATUS_TRANSITIONS[status] ?? [];
  return targets
    .map((target) => {
      const meta = ACTION_META[target];
      return meta ? { target, ...meta } : null;
    })
    .filter((action): action is OrderAction => action !== null);
}

export function isTerminalOrderStatus(status: string): boolean {
  return (ORDER_STATUS_TRANSITIONS[status] ?? []).length === 0;
}

/**
 * Human channel label for an order row: "Pickup", or the table snapshot for a
 * dine-in order. The label is used verbatim when it already starts with
 * "Table" so QR labels like "Table 5" are not doubled up.
 */
export function orderChannelLabel(order: { order_type: string; table_label: string | null }): string {
  if (order.order_type !== 'dine_in') return 'Pickup';
  const label = order.table_label?.trim();
  if (!label) return 'Dine in';
  return /^table\b/i.test(label) ? label : `Table ${label}`;
}

export function orderStatusTone(status: string): BadgeTone {
  switch (status) {
    case 'awaiting_payment':
      return 'warning';
    case 'placed':
      return 'info';
    case 'accepted':
      return 'info';
    case 'ready':
      return 'success';
    case 'completed':
      return 'default';
    case 'cancelled':
      return 'danger';
    case 'refunded':
      return 'danger';
    default:
      return 'default';
  }
}

export function reservationStatusTone(status: string): BadgeTone {
  switch (status) {
    case 'requested':
      return 'warning';
    case 'confirmed':
      return 'success';
    case 'declined':
      return 'danger';
    case 'cancelled':
      return 'danger';
    case 'seated':
      return 'info';
    case 'no_show':
      return 'default';
    default:
      return 'default';
  }
}

export function paymentStatusTone(status: string): BadgeTone {
  switch (status) {
    case 'paid':
      return 'success';
    case 'unpaid':
      return 'warning';
    case 'refunded':
    case 'partially_refunded':
      return 'danger';
    default:
      return 'default';
  }
}
